import type { FC, ReactNode } from 'react'
import type { Service } from '@/constants/content'

import Image from 'next/image'
import { useRef } from 'react'
import { motion, useInView } from 'motion/react'
import { services } from '@/constants/content'
import { Eyebrow } from '@/components/generals/eyebrow'
import { RevealText } from '@/components/generals/reveal'

const SOFT_EASE = [0.22, 1, 0.36, 1] as const

interface ServiceCardProps {
    service: Service
    index: number
}

/**
 * One full-width card. Every card is sticky a little lower than the one
 * before, so they pin and pile up on top of each other while scrolling.
 */
const ServiceCard: FC<ServiceCardProps> = ({ service, index }): ReactNode => {
    const ref = useRef<HTMLElement>(null)
    const inView = useInView(ref, { once: true, amount: 0.35 })
    const [first, second] = service.images

    return (
        <li className='sticky pb-5 md:pb-6' style={{ top: `calc(5.5rem + ${index} * 1.1rem)` }}>
            <article
                ref={ref}
                aria-labelledby={`service-${service.number}`}
                className='group relative flex h-[min(78svh,760px)] min-h-[500px] flex-col items-center justify-between overflow-hidden rounded-[14px] border border-white/10 bg-[#121212] px-6 py-8 shadow-[0_-30px_70px_-30px_rgba(0,0,0,0.9)] md:py-10'
            >
                <p className='text-sm tabular-nums text-muted-foreground'>
                    <span aria-hidden='true' className='mr-1.5 text-gold'>/</span>
                    {service.number}
                </p>

                <div className='relative flex flex-1 items-center justify-center py-6'>
                    {[first, second].map((image, imageIndex) => {
                        const tilt = imageIndex === 0 ? -20 : 20
                        const shift = imageIndex === 0 ? '18%' : '-18%'

                        return (
                            <motion.div
                                key={image.src}
                                className='relative aspect-[3/4] w-[clamp(120px,16vw,230px)] overflow-hidden rounded-[10px] bg-surface shadow-[0_30px_60px_-15px_rgba(0,0,0,0.85)]'
                                style={{ zIndex: imageIndex === 0 ? 1 : 2 }}
                                initial={{ opacity: 0, rotate: 0, x: '0%', y: 40 }}
                                animate={inView ? { opacity: 1, rotate: tilt, x: shift, y: 0 } : undefined}
                                whileHover={{ rotate: tilt * 0.6, scale: 1.04, transition: { duration: 0.6, ease: SOFT_EASE } }}
                                transition={{ duration: 1.1, delay: 0.1 + imageIndex * 0.1, ease: SOFT_EASE }}
                            >
                                <Image
                                    src={image.src}
                                    alt={image.alt}
                                    fill
                                    sizes='(min-width: 768px) 16vw, 120px'
                                    className='object-cover'
                                />
                            </motion.div>
                        )
                    })}
                </div>

                <div className='text-center'>
                    <h3
                        id={`service-${service.number}`}
                        className='text-[clamp(2rem,4.4vw,4rem)] font-light leading-none tracking-[-0.045em] text-foreground'
                    >
                        {service.title}
                    </h3>

                    <p className='mx-auto mt-4 max-w-md text-muted-foreground'>
                        {service.description}
                    </p>
                </div>
            </article>
        </li>
    )
}

export const Services: FC = (): ReactNode => {
    return (
        <section id='services' aria-labelledby='services-title' className='px-4 pb-16 pt-28 md:px-[30px] md:pb-24 md:pt-40'>
            <div className='flex flex-col items-center text-center'>
                <Eyebrow>Services</Eyebrow>

                <RevealText
                    id='services-title'
                    as='h2'
                    text='How I can help'
                    className='type-display mt-6'
                />
            </div>

            <ol className='mt-16 md:mt-24'>
                {services.map((service, index) => (
                    <ServiceCard key={service.number} service={service} index={index} />
                ))}
            </ol>
        </section>
    )
}
